import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdPhsInfoService } from '@/app/human-design/chart/bodygraph/hd-phs-info.service';
import { PHS_CATEGORIES, PhsCategory } from '@/app/human-design/chart/bodygraph/hd-phs-metadata';
import { HdPlanetInfoService } from '@/app/human-design/chart/bodygraph/hd-planet-info.service';
import { getHdPlanetDisplayConfig } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-display-config';

/** Resolved planet-label voor de huidige content-taal. */
interface PhsPlanetLabel {
  readonly glyph: string;
  readonly name: string;
}

/**
 * Resolved labels in de huidige content-taal. `*Secondary` is enkel gevuld in
 * NL-modus (toont dan de Engelse aanvulling tussen `/`); in EN-modus blijft die
 * leeg zodat enkel de Engelse term verschijnt.
 */
interface PhsBandLabels {
  readonly processPrimary: string;
  readonly processSecondary: string;
  readonly variablePrimary: string;
  readonly variableSecondary: string;
  readonly sideLabel: string;
  readonly planetA: PhsPlanetLabel;
  readonly planetB: PhsPlanetLabel;
  readonly cognitionLabel: string;
  readonly position: string;
  readonly partnerLabel: string;
}

/**
 * Eén PHS-transformatie als horizontale cheat-sheet-band — 1:1 naar het Figma-design
 * (node 2908:2): links de variabele + de 6 kleuren met links/rechts toon-keywords
 * (amber kleurnummer, grijze `|`-scheiders) gegroepeerd 1-3 / 4-6 met een accolade
 * + group keynote; rechts de cognitie/resonantie-rij met groep-onderschriften en het
 * brein-blok met de groene "tone/toon"-driehoek. Personality bands (motivatie + visie)
 * krijgen de blauwe partner-connectoren (1↔4, 2↔5, 3↔6) met overkoepelende accolade
 * + label "Overdracht" / "Transference" (motivatie) of "Afleiding" / "Distraction" (visie).
 *
 * Enige toevoeging t.o.v. het Figma: de vastgelegde FA-iconen. Tekst uit
 * {@link HdPhsInfoService} in de content-taal (primair); in NL-modus toont de
 * band naast de hoofdterm ook de Engelse vertaling als gloss/secondary, in
 * EN-modus blijven alleen de Engelse termen staan.
 */
@Component({
  selector: 'hd-phs-transformation-band',
  templateUrl: './hd-phs-transformation-band.component.html',
  styleUrl: './hd-phs-transformation-band.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-design]': "info().side === 'design'",
    '[class.is-personality]': "info().side === 'personality'",
    '[class.is-tinted]': 'tinted()',
  },
})
export class HdPhsTransformationBandComponent {
  readonly category = input.required<PhsCategory>();

  private readonly phsInfo = inject(HdPhsInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  private readonly altLang = computed(() => (this.lang() === 'en' ? 'nl' : 'en'));
  private readonly isNl = computed(() => this.lang() !== 'en');

  protected readonly info = computed(() => this.phsInfo.categoryInfoIn(this.category(), this.lang()));
  private readonly infoGloss = computed(() => this.phsInfo.categoryInfoIn(this.category(), this.altLang()));

  /** Resolved labels voor de huidige content-taal (NL toont "NL / EN", EN toont enkel EN). */
  protected readonly labels = computed<PhsBandLabels>(() => {
    const cat = this.category();
    const lang = this.lang();
    const altLang = this.altLang();
    const nl = this.isNl();
    const meta = PHS_CATEGORIES[cat];
    const primary = this.phsInfo.categoryLabelsIn(cat, lang);
    const secondary = nl ? this.phsInfo.categoryLabelsIn(cat, altLang) : null;
    const shared = this.phsInfo.sharedLabelsIn(lang);
    const planetCfgA = getHdPlanetDisplayConfig(meta.planets.a);
    const planetCfgB = getHdPlanetDisplayConfig(meta.planets.b);
    const planetNameA = this.planetInfo.planetInfoIn(meta.planets.a, lang).name;
    const planetNameB = this.planetInfo.planetInfoIn(meta.planets.b, lang).name;
    return {
      processPrimary: primary.processName,
      processSecondary: secondary?.processName ?? '',
      variablePrimary: primary.variableName,
      variableSecondary: secondary?.variableName ?? '',
      sideLabel: meta.side === 'design' ? shared.side.design : shared.side.personality,
      planetA: { glyph: planetCfgA.astronomiconChar, name: planetNameA },
      planetB: { glyph: planetCfgB.astronomiconChar, name: planetNameB },
      cognitionLabel: primary.cognitionLabel,
      position: primary.position,
      partnerLabel: primary.partnerLabel,
    };
  });

  /** Cognitie-groep-onderschriften in de huidige content-taal. */
  protected readonly cognitionGroups = computed(() =>
    this.phsInfo.sharedLabelsIn(this.lang()).cognitionGroups.map(g => ({
      label: `${g.name} / ${g.tone}`,
    })),
  );

  /** Even rijen (Omgeving, Visie) krijgen de lichte tint #F6F6F6 zoals in het Figma-design. */
  protected readonly tinted = computed(() => {
    const category = this.category();
    return category === 'environment' || category === 'view';
  });

  /**
   * Kleuren met optionele anderstalige naam erbij (gloss). Gloss wordt **alleen**
   * getoond in NL-modus (toont dan de Engelse aanvulling — cruciaal voor herkenning);
   * in EN-modus blijft de gloss leeg (de Nederlandse aanvulling is daar overbodig).
   */
  protected readonly colors = computed(() => {
    if (!this.isNl()) {
      return this.info().colors.map(color => ({ ...color, gloss: '' }));
    }
    const gloss = this.infoGloss().colors;
    return this.info().colors.map((color, i) => {
      const g = gloss[i]?.name ?? '';
      return { ...color, gloss: g && g.toLowerCase() !== color.name.toLowerCase() ? g : '' };
    });
  });

  /** Eerste 3 kleuren (groep "laag"). */
  protected readonly colorsLow = computed(() => this.colors().slice(0, 3));
  /** Laatste 3 kleuren (groep "hoog"). */
  protected readonly colorsHigh = computed(() => this.colors().slice(3, 6));
}
